'use client';

import React from 'react';
import Link from 'next/link';
import toast from 'react-hot-toast';
import type { AdminNavStatus } from '@/config/admin-navigation';

export interface AdminNavLinkProps {
  href: string;
  label?: string;
  status?: AdminNavStatus;
  isActive?: boolean;
  className?: string;
  activeClassName?: string;
  inactiveClassName?: string;
  disabledClassName?: string;
  onClick?: (e: React.MouseEvent) => void;
  children?: React.ReactNode;
  showBadge?: boolean;
  badgeText?: string;
  title?: string;
}

/**
 * Shared admin navigation link component.
 * - 'live' items render a real Next.js <Link> with prefetch={false} (prevents Next.js __next_tree.txt 404s).
 * - 'planned' items render a disabled non-anchor element with "Planned" / "Coming soon" status,
 *   preventing any prefetch requests and showing a feedback toast when clicked.
 */
export function AdminNavLink({
  href,
  label,
  status = 'live',
  isActive = false,
  className = '',
  activeClassName = '',
  inactiveClassName = '',
  disabledClassName = '',
  onClick,
  children,
  showBadge = false,
  badgeText = '',
  title,
}: AdminNavLinkProps) {
  const isLive = status === 'live';

  if (!isLive) {
    const handlePlannedClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      toast(`${label || 'This module'} is currently planned and coming soon!`, {
        icon: '⏳',
        duration: 2500,
        style: {
          borderRadius: '12px',
          background: '#1e293b',
          color: '#f8fafc',
          border: '1px solid #334155',
          fontSize: '13px',
          fontWeight: 600,
        },
      });
    };

    return (
      <button
        type="button"
        onClick={handlePlannedClick}
        title={title || `${label || 'Module'} (Planned — Coming soon)`}
        aria-disabled="true"
        className={`${className} ${
          disabledClassName || 'opacity-40 cursor-not-allowed select-none hover:bg-white/[0.02]'
        }`}
      >
        {children}
        {showBadge && (
          <span className="ml-auto inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-amber-500/15 text-amber-300 border border-amber-500/30 shrink-0">
            {badgeText}
          </span>
        )}
      </button>
    );
  }

  return (
    <Link
      href={href}
      prefetch={false}
      onClick={onClick}
      title={title}
      className={`${className} ${isActive ? activeClassName : inactiveClassName}`}
    >
      {children}
    </Link>
  );
}

export default AdminNavLink;
